import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { shouldShowRenameNotice } from '@/lib/rename'
import { cn } from '@/lib/utils'

type Phase = 'old' | 'out' | 'shrink' | 'in' | 'glow' | 'done'

// Headers remount per page; the morph plays once per launch, not on every navigation.
let playedThisLaunch = false

const OLD = 'Beacon'
const NEW = 'Faro'
const OUT_STAGGER = 90
const IN_STAGGER = 160
const LETTER_MS = 450
const SHRINK_MS = 600

// How long each phase lasts before the next one starts.
const PHASE_MS: Record<Exclude<Phase, 'done'>, number> = {
  old: 1000,
  out: OUT_STAGGER * (OLD.length - 1) + LETTER_MS + 150,
  shrink: SHRINK_MS,
  in: IN_STAGGER * (NEW.length - 1) + LETTER_MS,
  glow: 900,
}
const NEXT: Record<Exclude<Phase, 'done'>, Phase> = { old: 'out', out: 'shrink', shrink: 'in', in: 'glow', glow: 'done' }

// The header wordmark. While the rename card is still pending for this person, it
// first reads "Beacon", lets it dissolve letter by letter and lights "Faro" up in its
// place, tying the new name to the app they know (#585).
export function RenameWordmark() {
  const { user } = useAuth()
  const [animates] = useState(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (playedThisLaunch || reduced || !shouldShowRenameNotice(user)) return false
    playedThisLaunch = true
    return true
  })
  const [phase, setPhase] = useState<Phase>(animates ? 'old' : 'done')
  const [fontsReady, setFontsReady] = useState(false)
  const oldRef = useRef<HTMLSpanElement>(null)
  const newRef = useRef<HTMLSpanElement>(null)
  const [widths, setWidths] = useState<{ old: number; new: number } | null>(null)

  // Measured only once the web font is in: widths taken against the fallback font
  // are off by a few pixels, which shows as a jump when the morph starts and ends.
  useEffect(() => {
    if (!animates) return
    let live = true
    document.fonts.ready.then(() => live && setFontsReady(true))
    return () => {
      live = false
    }
  }, [animates])

  useLayoutEffect(() => {
    if (!fontsReady || widths || !oldRef.current || !newRef.current) return
    setWidths({ old: oldRef.current.offsetWidth, new: newRef.current.offsetWidth })
  }, [fontsReady, widths])

  useEffect(() => {
    if (phase === 'done' || !widths) return
    const t = setTimeout(() => setPhase(NEXT[phase]), PHASE_MS[phase])
    return () => clearTimeout(t)
  }, [phase, widths])

  if (!animates) return <>{NEW}</>

  const oldGone = phase !== 'old'
  // Beacon dissolves in place first; only then does the box close in around Faro's width.
  const shrunk = phase !== 'old' && phase !== 'out'
  const newShown = phase === 'in' || phase === 'glow' || phase === 'done'
  return (
    <span
      className="inline-grid justify-items-start whitespace-nowrap transition-[width] ease-in-out"
      // Both words share one grid cell, so before anything is measured the box is
      // already Beacon-wide. Once settled the width goes back to auto (Beacon is
      // hidden by then) and the letters keep their own spans, so nothing shifts.
      style={
        widths && phase !== 'done'
          ? { width: shrunk ? widths.new : widths.old, transitionDuration: `${SHRINK_MS}ms` }
          : undefined
      }
    >
      <span ref={oldRef} aria-hidden="true" className={cn('[grid-area:1/1]', phase === 'done' && 'hidden')}>
        {[...OLD].map((ch, i) => (
          <span
            key={i}
            className={cn(
              'inline-block transition-all ease-in',
              oldGone ? 'translate-y-1.5 opacity-0 blur-[3px]' : 'translate-y-0 opacity-100 blur-0',
            )}
            // Dissolves from the last letter back to the first.
            style={{ transitionDuration: `${LETTER_MS}ms`, transitionDelay: `${(OLD.length - 1 - i) * OUT_STAGGER}ms` }}
          >
            {ch}
          </span>
        ))}
      </span>
      <span ref={newRef} className="[grid-area:1/1]">
        {[...NEW].map((ch, i) => (
          <span
            key={i}
            className={cn(
              'inline-block transition-all ease-out',
              newShown ? 'translate-y-0 scale-100 opacity-100 blur-0' : '-translate-y-1 scale-110 opacity-0 blur-[6px]',
              phase === 'glow' && '[text-shadow:0_0_14px_var(--color-primary)]',
            )}
            style={{ transitionDuration: `${LETTER_MS}ms`, transitionDelay: phase === 'in' ? `${i * IN_STAGGER}ms` : '0ms' }}
          >
            {ch}
          </span>
        ))}
      </span>
    </span>
  )
}
